import React from 'react';
import PropTypes from 'prop-types';
import { useBoolean } from 'usehooks-ts';
import { useLocation, useHistory } from 'react-router-dom';

import AppMeta from 'components/App/Meta';
import AppSlideTab from 'components/App/AppSlideTab';
import AppLink from 'components/App/AppLink';
import Card from 'components/App/Card';
import Breadcrumb from 'components/App/Breadcrumb';
import ModalRequestAction from 'components/Modals/ModalRequestAction';
import AppLoading from 'components/App/Loading';
import PromotionDataTable from 'pages/Location/LocationDetail/Promotions/PromotionDataTable';
import Pagination from 'components/Pagination';

import { LOCATIONS } from 'constants/index';
import { PROMOTION_STATUS, PROMOTION_TAB_LIST } from 'constants/promotion';
import INITIAL_VALUE from 'constants/initialValue';
import { getParamsFormObject } from 'utils';
import cn from 'utils/classnames';
import totalPage from 'utils/totalPage';

import images from 'assets/images';

import styles from './Promotions.module.scss';

const BREADCRUMB = [
  {
    name: 'Chương trình khuyến mãi',
    isActive: false,
    link: '',
  },
  {
    name: 'Danh sách chương trình khuyến mãi',
    isActive: true,
    link: '',
  },
];

const DEFAULT_LIMIT = 20;
const GOING_PROMOTION_LIMIT = 100;

function Promotions(props) {
  const {
    isLoadingGetPromotionList,

    promotionList,
    promotionTotal,

    actionGetListPromotion,
    actionDeletePromotion,
    actionDeletePlacePromotion,
    actionDeletePromotionDiscount,
  } = props;

  const { search } = useLocation();
  const history = useHistory();
  const placeId = new URLSearchParams(search).get('place');
  const [promotionId, setPromotionId] = React.useState(null);
  const [promotionCategory, setPromotionCategory] = React.useState(null);

  const {
    value: isOpenEndModal,
    setTrue: onOpenEndModal,
    setFalse: onCloseEndModal,
    toggle: toggleEndModal,
  } = useBoolean(false);

  const {
    value: isOpenDeleteModal,
    setTrue: onOpenDeleteModal,
    setFalse: onCloseDeleteModal,
    toggle: toggleDeleteModal,
  } = useBoolean(false);

  const searchQuery = React.useMemo(() => {
    const params = new URLSearchParams(search);

    return {
      status: params.get('status') || PROMOTION_STATUS.GOING,
      place: params.get('place'),
      page: params.get('page') || 1,
    };
  }, [search]);

  const onChangeTab = React.useCallback(
    (status) => {
      history.push({
        search: getParamsFormObject({
          ...searchQuery,
          status,
          page: 1,
        }),
      });
    },
    [history, searchQuery],
  );

  const activeIndex = React.useMemo(
    () =>
      PROMOTION_TAB_LIST.findIndex((item) => item.value === searchQuery.status),
    [searchQuery],
  );

  const promotionLimit = React.useMemo(
    () => (
      searchQuery.status === PROMOTION_STATUS.GOING
        ? GOING_PROMOTION_LIMIT
        : DEFAULT_LIMIT
    ),
    [searchQuery.status],
  );

  const onChangePage = React.useCallback(
    (selected) => {
      history.push({
        search: getParamsFormObject({
          ...searchQuery,
          page: selected.selected + 1,
        }),
      });
    },
    [history, searchQuery],
  );

  const onEndPromotion = React.useCallback(
    (id, category) => () => {
      setPromotionId(id);
      setPromotionCategory(category);
      onOpenEndModal();
    },
    [],
  );

  const goToFirstPage = React.useCallback(() => {
    if (Number(searchQuery.page) === 1) {
      actionGetListPromotion({ ...searchQuery, limit: promotionLimit });
    } else {
      history.push({
        search: getParamsFormObject({ ...searchQuery, page: 1 }),
      });
    }
  }, [history, searchQuery, promotionLimit, actionGetListPromotion]);

  const onConfirmEndPromotion = React.useCallback(() => {
    const callback = () => {
      onCloseEndModal();
      goToFirstPage();
    };
    if (promotionCategory) {
      actionDeletePlacePromotion({
        promotionId,
        placeId,
        callback,
      });
    } else {
      actionDeletePromotion({
        promotionId,
        placeId,
        callback,
      });
    }
  }, [promotionId, placeId, promotionCategory, goToFirstPage]);

  const onDeletePromotion = React.useCallback(
    (id, category) => () => {
      setPromotionId(id);
      setPromotionCategory(category);
      onOpenDeleteModal();
    },
    [],
  );

  const onConfirmDeletePromotion = React.useCallback(() => {
    const callback = () => {
      onCloseDeleteModal();
      goToFirstPage();
    };
    if (promotionCategory) {
      actionDeletePlacePromotion({
        promotionId,
        placeId,
        status: 'PENDING',
        callback,
      });
    } else {
      actionDeletePromotionDiscount({
        promotionId,
        placeId,
        callback,
      });
    }
  }, [promotionId, placeId, promotionCategory, goToFirstPage]);

  React.useEffect(() => {
    actionGetListPromotion({
      ...searchQuery,
      limit: promotionLimit,
    });
  }, [searchQuery, promotionLimit]);

  return (
    <div className={cn('page-content', styles.Page)}>
      <AppMeta name="Khuyến mãi" />
      <Breadcrumb
        pageTitle="Khuyến mãi"
        breadcrumbList={BREADCRUMB}
        className={styles.BreadcrumbContainer}
      />

      <Card className={styles.Section}>
        <div className={styles.TitleWrap}>
          <div className={styles.Title}>
            <h3>Khuyến mãi của Bạn</h3>

            <p>Xem lại, quản lý và chọn khuyến mãi mới.</p>
          </div>

          <AppLink
            href={{
              pathname: LOCATIONS.LOCATION_DETAIL.CREATE_PROMOTION,
              search: `?place=${searchQuery.place}`,
            }}
            className={styles.CreateNewButton}
          >
            <i className="fas fa-plus" />
            Tạo khuyến mãi mới
          </AppLink>
        </div>

        <AppSlideTab
          listTab={PROMOTION_TAB_LIST}
          onSetValue={onChangeTab}
          activeIndex={activeIndex}
        />

        {isLoadingGetPromotionList ? (
          <AppLoading loading={isLoadingGetPromotionList} loaderOnPage />
        ) : (
          <PromotionDataTable
            tab={searchQuery.status}
            promotions={promotionList}
            onEndPromotion={onEndPromotion}
            onDeletePromotion={onDeletePromotion}
          />
        )}
      </Card>

      {promotionTotal > 0 && activeIndex !== 0 && (
        <Pagination
          page={searchQuery.page}
          pageCount={totalPage(promotionTotal, promotionLimit)}
          onChangePage={onChangePage}
        />
      )}

      <ModalRequestAction
        isOpen={isOpenEndModal}
        icon={images.modalWarning.default}
        textRequest={'Bạn có chắc chắn muốn ngừng \n khuyến mãi này không?'}
        onToggle={toggleEndModal}
        onCloseModal={onCloseEndModal}
        onConfirm={onConfirmEndPromotion}
      />

      <ModalRequestAction
        isOpen={isOpenDeleteModal}
        icon={images.modalWarning.default}
        textRequest={'Bạn có chắc chắn muốn xoá \n khuyến mãi này không?'}
        onToggle={toggleDeleteModal}
        onCloseModal={onCloseDeleteModal}
        onConfirm={onConfirmDeletePromotion}
      />
    </div>
  );
}

Promotions.propTypes = {
  isLoadingGetPromotionList: PropTypes.bool.isRequired,

  promotionList: PropTypes.instanceOf(Array),
  promotionTotal: PropTypes.number,

  actionGetListPromotion: PropTypes.func.isRequired,
  actionDeletePromotion: PropTypes.func.isRequired,
  actionDeletePlacePromotion: PropTypes.func.isRequired,
  actionDeletePromotionDiscount: PropTypes.func.isRequired,
};

Promotions.defaultProps = {
  promotionList: INITIAL_VALUE.ARRAY,
  promotionTotal: 0,
};

export default Promotions;
